// Mounts a playable match into a DOM element. Parameterized by the two fighters
// so the creator can run base-vs-generated playtests. The engine itself stays
// untouched — this is just the render/loop wiring that main.ts used to hold.
import { Container } from 'pixi.js';
import type { Character } from '../engine/schema.ts';
import { tick } from '../engine/tick.ts';
import { createWorld, GROUND_Y_SCREEN, STAGE_WIDTH, type World } from '../engine/world.ts';
import { lerp } from '../engine/vec.ts';
import { pollInputs, startKeyboard } from '../input/keyboard.ts';
import { cpuInput, type CpuLevel } from '../runtime/ai.ts';
import { startApp } from '../render/app.ts';
import { DebugOverlay } from '../render/debug.ts';
import { FighterRenderer } from '../render/fighter.ts';
import { HealthBars, MatchOverlay, RoundTimer } from '../render/hud.ts';
import { createStage } from '../render/stage.ts';
import { Background, type StageArt } from '../render/background.ts';
import { assertAtlasCoverage } from '../runtime/atlas.ts';
import { loadAtlasTextures } from '../runtime/assets.ts';
import { startLoop } from '../runtime/loop.ts';

export interface FighterSpec {
  character: Character;
  /** Resolved atlas URL; omit to render with procedural shapes. */
  atlasUrl?: string;
  color: number;
}

export interface MountOptions {
  p1: FighterSpec;
  p2: FighterSpec;
  /**
   * Showcase mode: the round timer never counts down (HUD reads "∞") and both
   * fighters' health is pinned full every tick, so the match never ends. Used by
   * the creator playtest so a new user can freely experiment with the controls.
   */
  unlimited?: boolean;
  /**
   * Stage art drawn behind the fighters (under the camera, so it pans/zooms with
   * the action). Omit for the phase-1 flat backdrop. Render-only — never touches
   * the deterministic sim.
   */
  stage?: StageArt;
  /**
   * Drive P2 with the built-in CPU instead of keyboard/gamepad input. The
   * stand-in opponent until online versus lands — the human plays P1, the CPU
   * plays P2. Toggleable live via the handle's `setCpuP2`.
   */
  cpuP2?: boolean;
  /** CPU difficulty (default 'normal'). Live-settable via `setCpuLevel`. */
  cpuLevel?: CpuLevel;
}

export interface GameHandle {
  reset: () => void;
  toggleDebug: () => void;
  /** Freeze the round timer so the match never times out — for continuous debugging. */
  setFreezeTimer: (on: boolean) => void;
  /** Pin both fighters' health full every tick so neither can be KO'd — for continuous debugging. */
  setInfiniteHealth: (on: boolean) => void;
  /** Drive P2 with the built-in CPU (true) or with keyboard/gamepad input (false). */
  setCpuP2: (on: boolean) => void;
  /** Set the CPU difficulty level. */
  setCpuLevel: (level: CpuLevel) => void;
  destroy: () => void;
}

// Follow-camera tuning. The view zooms in (up to MAX_ZOOM) when the fighters
// are close and eases out to the full stage (MIN_ZOOM = 1) as they separate, so
// both always stay framed. MARGIN is the world-px of breathing room kept around
// the pair; SMOOTH is the per-frame easing toward the target transform.
const MIN_ZOOM = 1;
const MAX_ZOOM = 1.9;
const MARGIN = 360;
const SMOOTH = 0.18;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Build the per-frame camera updater. Keeps its own eased {scale, x, y} state so
 * the zoom doesn't snap as the fighters move. Anchors the ground line at a fixed
 * screen y and pans horizontally to center the pair, clamped so the view never
 * shows past the stage edges.
 */
function updateCameraFn(camera: Container): (prev: World, curr: World, alpha: number) => void {
  let s = 1;
  let x = 0;
  let y = 0;
  let init = false;

  return (prev, curr, alpha) => {
    const x1 = lerp(prev.players[0]?.pos.x ?? 0, curr.players[0]?.pos.x ?? 0, alpha);
    const x2 = lerp(prev.players[1]?.pos.x ?? 0, curr.players[1]?.pos.x ?? 0, alpha);
    const sep = Math.abs(x1 - x2);
    const mid = (x1 + x2) / 2;

    const targetS = clamp(STAGE_WIDTH / (sep + MARGIN), MIN_ZOOM, MAX_ZOOM);
    // Center the pair, then clamp so [0, STAGE_WIDTH] never leaves the viewport.
    const targetX = clamp(STAGE_WIDTH / 2 - mid * targetS, STAGE_WIDTH * (1 - targetS), 0);
    const targetY = GROUND_Y_SCREEN * (1 - targetS); // keeps the ground line fixed

    if (!init) {
      s = targetS;
      x = targetX;
      y = targetY;
      init = true;
    } else {
      s += (targetS - s) * SMOOTH;
      x += (targetX - x) * SMOOTH;
      y += (targetY - y) * SMOOTH;
      // Re-clamp against the eased zoom so mid-transition never bleeds past edges.
      x = clamp(x, STAGE_WIDTH * (1 - s), 0);
    }

    camera.scale.set(s);
    camera.x = x;
    camera.y = y;
  };
}

export async function mountGame(mount: HTMLElement, opts: MountOptions): Promise<GameHandle> {
  const { p1, p2, unlimited = false, stage } = opts;
  let cpuP2 = opts.cpuP2 ?? false;
  let cpuLevel: CpuLevel = opts.cpuLevel ?? 'normal';
  for (const f of [p1, p2]) assertAtlasCoverage(f.character);

  // Engine character registry keyed by id. Distinct ids => both registered.
  const characters: Record<string, Character> = {
    [p1.character.meta.id]: p1.character,
    [p2.character.meta.id]: p2.character,
  };

  const [tex1, tex2] = await Promise.all([
    p1.atlasUrl && p1.character.spriteAtlas
      ? loadAtlasTextures(p1.atlasUrl, p1.character.spriteAtlas.frames)
      : Promise.resolve(undefined),
    p2.atlasUrl && p2.character.spriteAtlas
      ? loadAtlasTextures(p2.atlasUrl, p2.character.spriteAtlas.frames)
      : Promise.resolve(undefined),
  ]);

  const app = await startApp(mount);

  // Gameplay layer (stage + fighters + debug boxes) lives under a camera so it
  // can be zoomed/panned to frame the action. The HUD stays in screen space.
  const camera = new Container();
  app.stage.addChild(camera);
  // Stage art (if any) sits at the back of the camera so it pans/zooms with the
  // action; far layers parallax-drift via background.update each frame. The flat
  // phase-1 ground markers are the fallback when no stage is supplied.
  const background = stage ? await Background.create(stage) : null;
  camera.addChild(background ? background.view : createStage());

  const r1 = new FighterRenderer(p1.color, { character: p1.character, textures: tex1 });
  const r2 = new FighterRenderer(p2.color, { character: p2.character, textures: tex2 });
  camera.addChild(r1.view);
  camera.addChild(r2.view);
  const debugOverlay = new DebugOverlay();
  camera.addChild(debugOverlay.gfx);

  const healthBars = new HealthBars();
  app.stage.addChild(healthBars.gfx);
  const roundTimer = new RoundTimer(unlimited);
  app.stage.addChild(roundTimer.gfx);
  const matchOverlay = new MatchOverlay();
  app.stage.addChild(matchOverlay.gfx);

  startKeyboard(window);

  const updateCamera = updateCameraFn(camera);

  // When frozen, the round timer is restored after each tick so it never reaches
  // zero and times out the match — lets the sandbox run a round indefinitely.
  let freezeTimer = false;
  // When on, both fighters' health is pinned to its pre-tick value (they start
  // full, so restoring each tick = infinite HP) so a round never ends on a KO.
  let infiniteHealth = false;

  const handle = startLoop({
    createWorld: () => createWorld(p1.character.meta.id, p2.character.meta.id),
    pollInputs,
    tick: (w, inp) => {
      // P2 driven by the CPU: replace its polled buttons with the AI's decision
      // for this tick (P1 stays human). Done here, outside the pure tick, so the
      // engine still just consumes an Inputs struct.
      if (cpuP2) {
        inp = {
          players: [
            inp.players[0] ?? { buttons: 0 },
            { buttons: cpuInput(w, characters, 1, cpuLevel) },
          ],
        };
      }
      const before = w.roundTime;
      // Snapshot life before the tick so showcase mode can pin it back, keeping
      // both bars full (they start full, so restoring each tick = infinite HP).
      const beforeLife = w.players.map((p) => p?.life);
      const next = tick(w, characters, inp);
      if (freezeTimer || unlimited) next.roundTime = before;
      if (infiniteHealth || unlimited) {
        for (let i = 0; i < next.players.length; i++) {
          const p = next.players[i];
          const life = beforeLife[i];
          if (p && life !== undefined) p.life = life;
        }
      }
      return next;
    },
    render: (prev, curr, alpha) => {
      const a = prev.players[0];
      const b = curr.players[0];
      const c = prev.players[1];
      const d = curr.players[1];
      if (a && b) r1.update(a, b, alpha);
      if (c && d) r2.update(c, d, alpha);
      updateCamera(prev, curr, alpha);
      background?.update(camera.x, camera.scale.x);
      healthBars.update(curr);
      roundTimer.update(curr);
      matchOverlay.update(curr);
      debugOverlay.update(curr, characters);
    },
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'KeyR') handle.reset();
    if (e.code === 'F1' || e.code === 'Backquote') {
      e.preventDefault();
      debugOverlay.toggle();
    }
  };
  window.addEventListener('keydown', onKey);

  return {
    reset: () => handle.reset(),
    toggleDebug: () => debugOverlay.toggle(),
    setFreezeTimer: (on: boolean) => {
      freezeTimer = on;
    },
    setInfiniteHealth: (on: boolean) => {
      infiniteHealth = on;
    },
    setCpuP2: (on: boolean) => {
      cpuP2 = on;
    },
    setCpuLevel: (level: CpuLevel) => {
      cpuLevel = level;
    },
    destroy: () => {
      handle.stop();
      window.removeEventListener('keydown', onKey);
      app.destroy(true, { children: true });
    },
  };
}
